// @ts-strict-ignore
import React, {useCallback} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import {DragDropProvider, PointerSensor} from '@dnd-kit/react';

import * as BookmarkTypes from 'common/types/reports/bookmark';
import type {DragEndEvent} from 'common/components/draggable-list/types';
import {findKeyByValue} from 'common/util/typescript';
import {useTranslation} from 'common/i18n/hooks';
import {handleOnBeforeDrag} from 'common/components/draggable-list/utils';
import {reporter} from 'common/report/error-reporting';

import type {ReportState} from '../report-bookmark-manager';
import {
  handleFilterAcrossMetricEvents,
  handleGlobalFilerToMetricEvent,
  handleMetricEventDrop,
  handleMetricEventFiltersRearrange,
  handleMetricEventsRearrange,
} from './metric-events';
import {
  handleGlobalFiltersRearrange,
  handleGlobalFilterToBreakdowns,
  handleInlineFilterToBreakdowns,
  handleInlineFilterToGlobalFilters,
} from './filters';
import {handleMetricsRearrange} from './metrics';
import {BookmarkDragOperationMetadata, BookmarkDragOperationProps} from './types';
import {handleBreakdownsRearrange, handleBreakdownToFilter} from './breakdowns';

/**
 * DragDropProvider for handling drag and drop operations related to bookmarks in reports.
 * This takes care of interactions like dragging a filter onto a metric event, rearranging different sections throughout the report, etc...
 */
export function BookmarkDragDropProvider(props: {report: ReportState; children: React.ReactNode}) {
  const i18n = useTranslation();

  const {updateParams} = props.report;
  const params = cloneDeep(props.report.params);

  const onDragEnd = useCallback(
    (event: DragEndEvent<BookmarkDragOperationMetadata>) => {
      const isCloning = Boolean((event.nativeEvent as MouseEvent)?.altKey);
      const targetData = event.operation.target?.data as BookmarkDragOperationMetadata;
      const sourceData = event.operation.source?.data as BookmarkDragOperationMetadata;

      if (event.operation.canceled || !sourceData || !targetData) {
        return;
      }

      mixpanel.track(`[mp-query-builder] Drag operation`, {sourceData, targetData});

      const operationProps: BookmarkDragOperationProps = {
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData,
        isCloning,
      };

      /** rearrange metrics */
      if (
        findKeyByValue(BookmarkTypes.TopLevelMetricType, sourceData.type) &&
        findKeyByValue(BookmarkTypes.TopLevelMetricType, targetData.type)
      ) {
        return handleMetricsRearrange(operationProps);
      }

      /** perform an event filter drop onto an event inside a metric */
      if (sourceData.type === `inline-filter` && targetData.type === `behavior-event`) {
        return handleFilterAcrossMetricEvents(operationProps);
      }

      /** handles an inline filter onto a the global filter section */
      if (sourceData.type === `inline-filter` && targetData.type === `global-filters`) {
        return handleInlineFilterToGlobalFilters({
          i18n,
          event,
          params,
          updateParams,
          sourceData,
          targetData,
          isCloning,
        });
      }

      /** perform a global filter drop onto an event inside a metric */
      if (sourceData.type === `global-filter` && targetData.type === `behavior-event`) {
        return handleGlobalFilerToMetricEvent(operationProps);
      }

      /** rearrange global filters section */
      if (sourceData.type === `global-filter` && targetData.type === `global-filter`) {
        return handleGlobalFiltersRearrange(operationProps);
      }

      /** handle event drop onto a different metric */
      if (
        sourceData.type === `behavior-event` &&
        targetData.type === `behavior-event` &&
        targetData.group !== sourceData.group
      ) {
        handleMetricEventDrop(operationProps);
      }

      /** rearrange events inside a metric */
      if (
        sourceData.type === `behavior-event` &&
        targetData.type === `behavior-event` &&
        targetData.group === sourceData.group
      ) {
        handleMetricEventsRearrange(operationProps);
      }

      /** rearrange filters inside a metric event */
      if (
        sourceData.type === `inline-filter` &&
        targetData.type === `inline-filter` &&
        targetData.behaviorIndex?.toString() === sourceData.behaviorIndex?.toString() &&
        targetData.group === sourceData.group
      ) {
        handleMetricEventFiltersRearrange({
          i18n,
          event,
          params,
          updateParams,
          sourceData,
          targetData,
          isCloning,
        });
      }

      /** handles a global filter onto the breakdown section */
      if (sourceData.type === `global-filter` && targetData.type === `global-breakdowns`) {
        handleGlobalFilterToBreakdowns(operationProps);
      }

      /** handles an inline filter onto the breakdown section */
      if (sourceData.type === `inline-filter` && targetData.type === `global-breakdowns`) {
        handleInlineFilterToBreakdowns(operationProps);
      }

      /** rearrange global breakdown section */
      if (sourceData.type === `breakdown` && targetData.type === `breakdown`) {
        return handleBreakdownsRearrange(operationProps);
      }

      /** handles a breakdown onto a filter section */
      if (
        sourceData.type === `breakdown` &&
        (targetData.type === `global-filters` || targetData.type === `behavior-event`)
      ) {
        return handleBreakdownToFilter(operationProps);
      }
    },
    [props.report],
  );

  return (
    <DragDropProvider
      /** prevents drag operations from inner elements where dragging should not be initiated form */
      onBeforeDragStart={handleOnBeforeDrag}
      /** allows drag operations to start from the element itself and not just the drag handle */
      sensors={[
        PointerSensor.configure({
          activatorElements(source) {
            return [source.handle, source.element];
          },
        }),
      ]}
      /** captures any exception in drop operations to prevent the UI from freezing */
      onDragEnd={(event) => {
        try {
          onDragEnd(event);
        } catch (error) {
          reporter.error(`[mp-query-builder] Failed Drag Operation`, {
            error: String(error),
            sourceData: event.operation.source?.data,
            targetData: event.operation.target?.data,
          });
        }
      }}
    >
      {props.children}
    </DragDropProvider>
  );
}
